import { Badge as BadgeIcon } from '@icon-park/react'
import { ReactNode, useEffect, useRef, useState } from 'react'
import Capitalize from '@stdlib/string-capitalize'
import { Button } from '@/components'
import { Api } from '@/modules'
import { MentionHistoryEntry } from '@shared/models/mentions'
import {
  PopoverContainer,
  PopoverTrigger,
  PopoverBalloon,
  PopoverArrow,
  Entry,
  EntryMeta,
  EntryDetails,
  Loading,
  Empty,
} from './HistoryPopover.styles'

export type HistoryPopoverProps = {
  companyID: string
  mentionID: string
  placement?: 'left' | 'right'
  triggerIcon?: ReactNode
  className?: string
  brandInternalID: number
}

const formatFieldValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '—'
  }

  const stringValue = String(value)

  return Capitalize(stringValue.toLowerCase())
}

export function HistoryPopover({
  companyID,
  mentionID,
  placement = 'right',
  triggerIcon,
  className,
  brandInternalID,
}: HistoryPopoverProps) {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<MentionHistoryEntry[] | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [loadHistory, isPending, data, error, abort] = Api.LazyQuery<MentionHistoryEntry[]>(
    `companies/${companyID}/mentions/${mentionID}/history?brandInternalID=${brandInternalID}`,
  )

  useEffect(() => {
    if (open) {
      setEntries(null)
      loadHistory()
    }
  }, [open, companyID, mentionID, brandInternalID])

  useEffect(() => {
    if (isPending) return
    if (data) {
      setEntries(data)
    } else if (!isPending && error) {
      setEntries([])
    }
  }, [data, error, isPending])

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  useEffect(() => {
    return () => {
      try {
        abort && abort()
      } catch {
        // ignore abort errors
      }
    }
  }, [abort])

  const toggle = () => setOpen((v) => !v)

  return (
    <PopoverContainer ref={containerRef} className={className}>
      <PopoverTrigger onClick={toggle} aria-expanded={open}>
        {triggerIcon ?? <Button.Icon value={BadgeIcon} size={18} color={undefined} />}
      </PopoverTrigger>

      {open && (
        <PopoverBalloon $placement={placement} role="dialog" aria-label="Histórico de alterações">
          <PopoverArrow $placement={placement} />

          {isPending && <Loading>Carregando histórico...</Loading>}

          {!isPending && (!entries || entries.length === 0) && <Empty>Nenhuma alteração encontrada.</Empty>}

          {!isPending && entries && entries.length > 0 && (
            <>
              {entries.map((e, idx) => (
                <Entry key={`${e.version ?? idx}-${idx}`}>
                  <EntryMeta>
                    <strong>{e.changedByUsername || 'Usuário desconhecido'}</strong>
                    <span>{new Date(e.changedAt).toLocaleString('pt-BR')}</span>
                  </EntryMeta>

                  <EntryDetails>
                    {e.changedFields && e.changedFields.length > 0 && (
                      <div className="fields">
                        <strong>Campos alterados:</strong>{' '}
                        {e.changedFields.map((field) => Capitalize(field.toLowerCase())).join(', ')}
                      </div>
                    )}

                    {e.diff && Object.keys(e.diff).length > 0 ?
                      <div className="diff-preview">
                        {Object.entries(e.diff).map(([k, v]) => (
                          <div key={k} className="diff-row">
                            <div className="diff-field">{Capitalize(k.toLowerCase())}</div>
                            <div
                              className="diff-values"
                              title={`Antes: ${formatFieldValue(v.old)} | Depois: ${formatFieldValue(v.new)}`}>
                              {formatFieldValue(v.old)} → {formatFieldValue(v.new)}
                            </div>
                          </div>
                        ))}
                      </div>
                    : <div className="no-diff">Nenhuma diferença detalhada disponível.</div>}
                  </EntryDetails>
                </Entry>
              ))}
            </>
          )}
        </PopoverBalloon>
      )}
    </PopoverContainer>
  )
}

export default HistoryPopover
